import { inject, injectable } from "inversify";
import { DependencyType } from "@di/DependencyType";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import {
  CreateSoilSampleDocument,
  CreateSoilSampleInput,
  CreateSoilSampleMutation,
  CreateSoilSampleMutationVariables,
  SoilSample,
  SoilSampleDocument,
  SoilSampleQuery,
  SoilSampleQueryVariables,
  SoilSamplesDocument,
  SoilSamplesQuery,
  SoilSamplesQueryVariables,
  UpdateSoilSampleDocument,
  UpdateSoilSampleInput,
  UpdateSoilSampleMutation,
  UpdateSoilSampleMutationVariables,
  SoilSampleSortInput,
  SortEnumType,
} from "generated/graphql";
import { PartialDeep } from "type-fest";

// Simple pagination result type
export interface SoilSamplePage {
  nodes: PartialDeep<SoilSample, { recurseIntoArrays: true }>[];
  hasNextPage: boolean;
  endCursor?: string | null;
}

// Sort key type for soil samples
export type SoilSampleSortKey = "name" | "createdAt" | "updatedAt";

@injectable()
export class SoilSampleService {
  constructor(
    @inject(DependencyType.ApolloClient)
    private readonly apolloClient: ApolloClient<NormalizedCacheObject>
  ) {}

  async findMany(
    after?: string,
    search?: string,
    sortKey?: SoilSampleSortKey,
    sortDirection?: "ASC" | "DESC"
  ): Promise<SoilSamplePage> {
    const normalizedSearch = search?.trim();

    // An omitted order lets the backend apply relevance ordering for searches.
    const order: SoilSampleSortInput[] | undefined = sortKey
      ? [
          {
            [sortKey]:
              sortDirection === "DESC" ? SortEnumType.Desc : SortEnumType.Asc,
          },
        ]
      : undefined;

    const queryTuple = await this.apolloClient.query<
      SoilSamplesQuery,
      SoilSamplesQueryVariables
    >({
      query: SoilSamplesDocument,
      variables: {
        after: after ?? null,
        search: normalizedSearch || null,
        where: null,
        order,
      },
    });

    if (queryTuple.errors) {
      throw new Error(queryTuple.errors[0].message);
    }

    const connection = queryTuple.data.soilSamples;
    const nodes = (connection?.nodes ?? []) as PartialDeep<
      SoilSample,
      { recurseIntoArrays: true }
    >[];

    return {
      nodes,
      hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
      endCursor: connection?.pageInfo?.endCursor,
    };
  }

  async findOne(
    id: string
  ): Promise<PartialDeep<SoilSample, { recurseIntoArrays: true }> | null> {
    const normalizedId = id.trim();
    if (!normalizedId) {
      return null;
    }

    console.log("Finding soil sample with id", normalizedId);
    const queryTuple = await this.apolloClient.query<
      SoilSampleQuery,
      SoilSampleQueryVariables
    >({
      query: SoilSampleDocument,
      variables: {
        id: normalizedId,
      },
    });

    if (queryTuple.errors) {
      throw new Error(queryTuple.errors[0].message);
    }
    if (!queryTuple.data.soilSample) {
      return null;
    }

    return queryTuple.data.soilSample;
  }

  async create(
    input: CreateSoilSampleInput
  ): Promise<PartialDeep<SoilSample, { recurseIntoArrays: true }>> {
    const mutationTuple = await this.apolloClient.mutate<
      CreateSoilSampleMutation,
      CreateSoilSampleMutationVariables
    >({
      mutation: CreateSoilSampleDocument,
      variables: {
        input,
      },
      update: (cache) => {
        cache.evict({ id: "ROOT_QUERY", fieldName: "soilSamples" });
      },
    });

    if (mutationTuple.errors) {
      throw new Error(mutationTuple.errors[0].message);
    }
    if (!mutationTuple.data?.createSoilSample) {
      throw new Error("Failed to create soil sample");
    }

    return mutationTuple.data?.createSoilSample;
  }

  async update(
    input: UpdateSoilSampleInput
  ): Promise<PartialDeep<SoilSample, { recurseIntoArrays: true }>> {
    const mutationTuple = await this.apolloClient.mutate<
      UpdateSoilSampleMutation,
      UpdateSoilSampleMutationVariables
    >({
      mutation: UpdateSoilSampleDocument,
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: SoilSampleDocument,
          variables: {
            id: input.id,
          },
        },
      ],
      update: (cache) => {
        cache.evict({ id: "ROOT_QUERY", fieldName: "soilSamples" });
      },
      awaitRefetchQueries: true,
    });

    if (mutationTuple.errors) {
      throw new Error(mutationTuple.errors[0].message);
    }
    if (!mutationTuple.data?.updateSoilSample) {
      throw new Error("Failed to update soil sample");
    }

    return mutationTuple.data?.updateSoilSample;
  }
}
